import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Users,
    Briefcase,
    ClipboardList,
    Puzzle,
    ArrowRight,
    ArrowUp,
    ArrowDown,
    BarChart3,
    Calendar,
    Building2, // Added for Add Company
    Sparkles, // Added for View Matches
    KanbanSquare, // Added for View Kanban
    Loader2, // Added for loading states in modals if needed
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { Link, useNavigate } from "react-router-dom"; // Added useNavigate
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Import hooks and components for quick actions
import { useCandidates } from "@/hooks/candidates/useCandidates";
import { useCompanies } from "@/hooks/useCompanies";
import { useJobsData } from "@/hooks/jobs/useJobsData";
import { AddCandidateSelectionModal } from "@/components/candidates/AddCandidateSelectionModal";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { CandidateFormData } from "@/utils/candidate";
import { Candidate } from "@/types/candidate";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { toast } from "@/hooks/use-toast";

const Dashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate(); // Initialize navigate
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const [chartView, setChartView] = useState<"day" | "week" | "month">("day");

    // Hooks for data fetching and mutations (needed for quick actions)
    // Note: useCandidates hook might need adjustment if it wasn't designed for mutations here
    const { candidates, setCandidates } = useCandidates(); // Assuming useCandidates provides setCandidates
    const { addCompany, fetchCompanies } = useCompanies(); // Assuming useCompanies provides these
    const { addJob } = useJobsData(); // Assuming useJobsData provides addJob

    // State for modals
    const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
    const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
    const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
    const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
    const [prefillFormData, setPrefillFormData] =
        useState<CandidateFormData | null>(null);

    // Fetch dashboard stats (existing code)
    const { data: statsData, isLoading: statsLoading } = useQuery({
        queryKey: ["dashboardStats"],
        queryFn: async () => {
            // ... existing stats fetching logic ...
            try {
                // Fetch candidates count
                const { count: candidatesCount, error: candidatesError } =
                    await supabase
                        .from("candidates")
                        .select("*", { count: "exact", head: true });

                if (candidatesError) throw candidatesError;

                // Fetch companies count
                const { count: companiesCount, error: companiesError } =
                    await supabase
                        .from("companies")
                        .select("*", { count: "exact", head: true });

                if (companiesError) throw companiesError;

                // Fetch jobs count
                const { count: jobsCount, error: jobsError } = await supabase
                    .from("jobs")
                    .select("*", { count: "exact", head: true });

                if (jobsError) throw jobsError;

                // Fetch matches count
                const { count: matchesCount, error: matchesError } =
                    await supabase
                        .from("matches")
                        .select("*", { count: "exact", head: true });

                if (matchesError) throw matchesError;

                return [
                    {
                        name: "Candidates",
                        count: candidatesCount || 0,
                        icon: Users,
                        color: "bg-blue-50 text-blue-500",
                        change: 12, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/candidates", // Added link for consistency
                    },
                    {
                        name: "Companies",
                        count: companiesCount || 0,
                        icon: Building2, // Changed icon to Building2 for consistency
                        color: "bg-purple-50 text-purple-500",
                        change: 5, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/companies", // Added link for consistency
                    },
                    {
                        name: "Jobs",
                        count: jobsCount || 0,
                        icon: Briefcase, // Changed icon to Briefcase for consistency
                        color: "bg-amber-50 text-amber-500",
                        change: 3, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/jobs", // Added link for consistency
                    },
                    {
                        name: "Matches",
                        count: matchesCount || 0,
                        icon: Sparkles, // Changed icon to Sparkles for consistency
                        color: "bg-emerald-50 text-emerald-500",
                        change: 8, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/matches", // Added link for consistency
                    },
                ];
            } catch (error) {
                console.error("Error fetching dashboard stats:", error);
                // Return default structure on error to prevent crashes
                return [
                    { name: "Candidates", count: 0, icon: Users, color: "bg-blue-50 text-blue-500", change: 0, isPositive: true, link: "/candidates" },
                    { name: "Companies", count: 0, icon: Building2, color: "bg-purple-50 text-purple-500", change: 0, isPositive: true, link: "/companies" },
                    { name: "Jobs", count: 0, icon: Briefcase, color: "bg-amber-50 text-amber-500", change: 0, isPositive: true, link: "/jobs" },
                    { name: "Matches", count: 0, icon: Sparkles, color: "bg-emerald-50 text-emerald-500", change: 0, isPositive: true, link: "/matches" },
                ];
            }
        },
    });

    // Fetch matches data for chart based on selected view
    const { data: matchesData, isLoading: matchesLoading } = useQuery({
        queryKey: ["dashboardMatches", chartView],
        queryFn: async () => {
            try {
                const { data, error } = await supabase
                    .from("matches")
                    .select("created_at");

                if (error) throw error;

                if (!data) return [];

                if (chartView === "month") {
                    // Group matches by month
                    const monthMap = new Map();
                    const months = [
                        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
                    ];

                    data.forEach((match) => {
                        const date = new Date(match.created_at);
                        const month = date.getMonth();
                        const monthName = months[month];

                        if (!monthMap.has(monthName)) {
                            monthMap.set(monthName, 0);
                        }

                        monthMap.set(
                            monthName,
                            monthMap.get(monthName) + 1
                        );
                    });

                    // Convert to chart data format
                    const chartData = [];
                    for (let i = 0; i < 12; i++) {
                        const monthName = months[i];
                        chartData.push({
                            month: monthName,
                            matches: monthMap.get(monthName) || 0,
                        });
                    }

                    return chartData;
                }
                
                if (chartView === "week") {
                    // Get data for last 10 weeks
                    const weekMap = new Map();
                    const now = new Date();
                    
                    // Create labels for the last 10 weeks
                    const weekLabels = [];
                    for (let i = 9; i >= 0; i--) {
                        const weekStart = new Date();
                        weekStart.setDate(now.getDate() - (i * 7));
                        const weekLabel = `W${getWeekNumber(weekStart)}`;
                        weekLabels.push(weekLabel);
                        weekMap.set(weekLabel, 0);
                    }
                    
                    // Count matches for each week
                    data.forEach((match) => {
                        const matchDate = new Date(match.created_at);
                        // Filter only matches from the last 10 weeks
                        if (now.getTime() - matchDate.getTime() <= 10 * 7 * 24 * 60 * 60 * 1000) {
                            const weekLabel = `W${getWeekNumber(matchDate)}`;
                            if (weekMap.has(weekLabel)) {
                                weekMap.set(weekLabel, weekMap.get(weekLabel) + 1);
                            }
                        }
                    });
                    
                    // Convert to chart data
                    return weekLabels.map(label => ({
                        month: label, // Reusing "month" key for compatibility
                        matches: weekMap.get(label) || 0
                    }));
                }
                
                if (chartView === "day") {
                    // Get data for last 30 days
                    const dayMap = new Map();
                    const now = new Date();
                    
                    // Create labels for the last 30 days
                    const dayLabels = [];
                    for (let i = 29; i >= 0; i--) {
                        const day = new Date();
                        day.setDate(now.getDate() - i);
                        const dayLabel = day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        dayLabels.push(dayLabel);
                        dayMap.set(dayLabel, 0);
                    }
                    
                    // Count matches for each day
                    data.forEach((match) => {
                        const matchDate = new Date(match.created_at);
                        // Filter only matches from the last 30 days
                        if (now.getTime() - matchDate.getTime() <= 30 * 24 * 60 * 60 * 1000) {
                            const dayLabel = matchDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            if (dayMap.has(dayLabel)) {
                                dayMap.set(dayLabel, dayMap.get(dayLabel) + 1);
                            }
                        }
                    });
                    
                    // Convert to chart data
                    return dayLabels.map(label => ({
                        month: label, // Reusing "month" key for compatibility
                        matches: dayMap.get(label) || 0
                    }));
                }
                
                return [];
            } catch (error) {
                console.error("Error fetching matches data:", error);
                return [];
            }
        },
    });
    
    // Helper function to get week number of a date
    const getWeekNumber = (date: Date) => {
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    };

    // Fetch upcoming events (existing code)
    const { data: upcomingEvents, isLoading: eventsLoading } = useQuery({
        queryKey: ["upcomingEvents"],
        queryFn: async () => {
            // ... existing events fetching logic ...
            try {
                const now = new Date();
                const twoWeeksFromNow = new Date();
                twoWeeksFromNow.setDate(now.getDate() + 14);

                // Using actions table with Calendar type and d_begin field instead of start_date
                const { data, error } = await supabase
                    .from("actions")
                    .select("*")
                    .eq("action_type", "Calendar")
                    .gte("d_begin", now.toISOString())
                    .lte("d_begin", twoWeeksFromNow.toISOString())
                    .order("d_begin", { ascending: true })
                    .limit(3);

                if (error) throw error;

                return data || [];
            } catch (error) {
                console.error("Error fetching upcoming events:", error);
                return [];
            }
        },
    });

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Handlers from previous version
    const handleOpenAddCandidateModal = () => {
        setIsSelectionModalOpen(true);
    };

    const handleManualCreate = () => {
        setIsSelectionModalOpen(false);
        setPrefillFormData(null);
        setIsAddFormModalOpen(true);
    };

    const handleCVUpload = (formData: CandidateFormData) => {
        setIsSelectionModalOpen(false);
        setPrefillFormData(formData);
        setIsAddFormModalOpen(true);
    };

    const handleCandidateAdded = (newCandidate: Candidate) => {
        // Optimistically update candidate list or refetch
        if (setCandidates) {
            setCandidates((prev: Candidate[] | undefined) => [
                ...(prev || []),
                newCandidate,
            ]);
        }
        setIsAddFormModalOpen(false);
        setPrefillFormData(null);
        toast({
            title: "Success",
            description: "Candidate added successfully.",
        });
        // Optionally navigate: navigate(`/candidates/${newCandidate.id}`);
    };

    const handleOpenAddCompanyModal = () => {
        setIsAddCompanyModalOpen(true);
    };

    const handleCompanySubmit = async (companyData: any) => {
        try {
            const success = await addCompany(companyData); // Assumes addCompany returns boolean/data
            if (success) {
                await fetchCompanies(); // Refetch companies list
                toast({
                    title: "Success",
                    description: "Company created successfully",
                });
                setIsAddCompanyModalOpen(false); // Close modal on success
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error adding company:", error);
            toast({
                title: "Error",
                description: "Failed to create company",
                variant: "destructive",
            });
            return false;
        }
    };

    const handleOpenAddJobModal = () => {
        setIsAddJobModalOpen(true);
    };

    const handleAddJob = async (jobData: any) => {
        try {
            const jobId = await addJob(jobData); // Assumes addJob returns ID or data
            if (jobId) {
                toast({
                    title: "Success",
                    description: "Job offer created successfully",
                });
                setIsAddJobModalOpen(false); // Close modal on success
                // Optionally refetch jobs or navigate
                return jobId;
            }
            return undefined;
        } catch (error) {
            console.error("Error adding job:", error);
            toast({
                title: "Error",
                description: "Failed to create job offer",
                variant: "destructive",
            });
            return undefined;
        }
    };

    const statisticsData = statsData || [];
    const chartData = matchesData || [];
    const events = upcomingEvents || [];

    return (
        <div className="space-y-8">
            <PageHeader
                title={t("dashboard.title")}
                description={t("dashboard.welcome")}
            />

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statsLoading
                    ? // Loading placeholders (existing code)
                      Array(4)
                            .fill(0)
                            .map((_, i) => (
                                <Card key={i} className="animate-pulse">
                                    <CardHeader className="pb-2">
                                        <div className="flex justify-between items-start">
                                            <div className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 h-8 w-8"></div>
                                            <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-1"></div>
                                        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    </CardContent>
                                    {/* Add CardFooter placeholder if needed */}
                                </Card>
                            ))
                    : statisticsData.map((stat) => (
                            <Card key={stat.name}>
                                <CardHeader className="pb-2">
                                    <div className="flex justify-between items-start">
                                        <div
                                            className={cn(
                                                "p-2 rounded-lg",
                                                stat.color
                                            )}
                                        >
                                            <stat.icon className="h-5 w-5" />
                                        </div>
                                        {/* Optional: Keep change indicator if data is available */}
                                        {/* <div className="flex items-center text-sm"> ... </div> */}
                                    </div>
                                </CardHeader>
                                <CardContent className="pb-3">
                                    <div className="text-3xl font-semibold mb-1">
                                        {stat.count}
                                    </div>
                                    <p className="text-muted-foreground text-sm">
                                        {t(`dashboard.stats.${stat.name.toLowerCase()}`)}
                                    </p>
                                </CardContent>
                                <CardFooter>
                                    <Button
                                        asChild
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs text-muted-foreground p-0 h-auto hover:bg-transparent"
                                    >
                                        <Link to={stat.link || "#"}>
                                            {t("dashboard.viewAll")}
                                            <ArrowRight className="ml-1 h-3 w-3" />
                                        </Link>
                                    </Button>
                                </CardFooter>
                            </Card>
                      ))}
            </div>

            {/* Chart, Events, and Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Matches Overview Card (existing code) */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <div>
                                <CardTitle>{t("dashboard.matchesOverview")}</CardTitle>
                                <CardDescription>
                                    {chartView === "day" ? t("dashboard.dailyMatches") : 
                                     chartView === "week" ? t("dashboard.weeklyMatches") : 
                                     t("dashboard.monthlyMatches")}
                                </CardDescription>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="flex items-center rounded-md border">
                                    <Button 
                                        variant={chartView === "day" ? "secondary" : "ghost"}
                                        size="sm"
                                        className="rounded-r-none"
                                        onClick={() => setChartView("day")}
                                    >
                                        {t("dashboard.day")}
                                    </Button>
                                    <Button
                                        variant={chartView === "week" ? "secondary" : "ghost"}
                                        size="sm"
                                        className="rounded-none border-x"
                                        onClick={() => setChartView("week")}
                                    >
                                        {t("dashboard.week")}
                                    </Button>
                                    <Button
                                        variant={chartView === "month" ? "secondary" : "ghost"}
                                        size="sm"
                                        className="rounded-l-none"
                                        onClick={() => setChartView("month")}
                                    >
                                        {t("dashboard.month")}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            {matchesLoading ? (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={chartData}
                                        margin={{
                                            top: 20,
                                            right: 15,
                                            left: 0,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            opacity={0.1}
                                        />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor:
                                                    "rgba(255, 255, 255, 0.8)",
                                                borderRadius: "0.5rem",
                                                border: "none",
                                                boxShadow:
                                                    "0 4px 12px rgba(0, 0, 0, 0.05)",
                                            }}
                                        />
                                        <Bar
                                            dataKey="matches"
                                            fill="hsl(var(--primary))"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="ml-auto"
                        >
                            <Link to="/matches">
                                {t("dashboard.viewAllMatches")}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    </CardFooter>
                </Card>

                {/* Quick Actions Card (New) */}
                <Card>
                    <CardHeader>
                        <CardTitle>{t("dashboard.quickActions")}</CardTitle>
                        <CardDescription>
                            {t("dashboard.quickActionsDesc")}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-y-3">
                        <Button
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                            onClick={handleOpenAddCandidateModal}
                        >
                            <Users className="mr-2 h-4 w-4" />
                            {t("dashboard.addCandidate")}
                        </Button>
                        <Button
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                            onClick={handleOpenAddCompanyModal}
                        >
                            <Building2 className="mr-2 h-4 w-4" />
                            {t("dashboard.addCompany")}
                        </Button>
                        <Button
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                            onClick={handleOpenAddJobModal}
                        >
                            <Briefcase className="mr-2 h-4 w-4" />
                            {t("dashboard.addJobOffer")}
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                        >
                            <Link to="/matches">
                                <Sparkles className="mr-2 h-4 w-4" />
                                {t("dashboard.viewMatches")}
                            </Link>
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                        >
                            <Link to="/kanban">
                                <KanbanSquare className="mr-2 h-4 w-4" />
                                {t("dashboard.viewKanban")}
                            </Link>
                        </Button>
                    </CardContent>
                    {/* No Footer needed for Quick Actions? Or add a general link? */}
                </Card>

                {/* Upcoming Events Card (Moved down to fit grid) */}
                <Card className="lg:col-span-3"> {/* Span full width below */}
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <div>
                                <CardTitle>{t("dashboard.upcomingEvents")}</CardTitle>
                                <CardDescription>
                                    {t("dashboard.next14Days")}
                                </CardDescription>
                            </div>
                            <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                    </CardHeader>
                    <CardContent>
                        {eventsLoading ? (
                            <div className="space-y-4">
                                {Array(3)
                                    .fill(0)
                                    .map((_, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-3 animate-pulse"
                                        >
                                            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-2 h-8 w-8"></div>
                                            <div className="space-y-2 flex-1">
                                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ) : events.length > 0 ? (
                            <div className="space-y-4">
                                {events.map((event) => {
                                    const eventDate = new Date(event.d_begin);
                                    const formattedDate =
                                        new Intl.DateTimeFormat("en-US", {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        }).format(eventDate);

                                    return (
                                        <div
                                            key={event.id}
                                            className="flex items-start gap-3"
                                        >
                                            <div className="bg-blue-50 rounded-lg p-2 mt-1">
                                                <Calendar className="h-4 w-4 text-blue-500" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-sm">
                                                    {event.name}
                                                </h3>
                                                <p className="text-xs text-muted-foreground">
                                                    {formattedDate}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center text-muted-foreground py-8">
                                {t("dashboard.noUpcomingEvents")}
                            </div>
                        )}
                    </CardContent>
                    <CardFooter>
                        <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="ml-auto"
                        >
                            <Link to="/calendar">
                                {t("dashboard.viewCalendar")}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    </CardFooter>
                </Card>
            </div>

            {/* Modals */}
            <AddCandidateSelectionModal
                isOpen={isSelectionModalOpen}
                onClose={() => setIsSelectionModalOpen(false)}
                onManualCreate={handleManualCreate}
                onCVUpload={handleCVUpload}
            />

            <AddCandidateForm
                isOpen={isAddFormModalOpen}
                onClose={() => setIsAddFormModalOpen(false)}
                onCandidateAdded={handleCandidateAdded}
                initialFormData={prefillFormData}
            />

            <AddCompanyForm
                isOpen={isAddCompanyModalOpen}
                onClose={() => setIsAddCompanyModalOpen(false)}
                onSubmit={handleCompanySubmit}
            />

            <JobOffersAddModal
                isOpen={isAddJobModalOpen}
                onClose={() => setIsAddJobModalOpen(false)}
                onAddJob={handleAddJob}
            />
        </div>
    );
};

export default Dashboard;